<script lang="ts">
const __DsBlock = {
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script setup lang="ts">
import type { RelatedDisplayItem } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { FormSaveContext, FormSaveError } from '#layers/forms/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay, useStore } from '#imports';
import { canAssignPolicy, connectRolePolicies, disconnectRolePolicies, getKeyId } from '#layers/access/public';
import { toStructuredError } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useCollectionByName } from '#layers/data-model/public';
import { injectFormBlockContext, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';
import { buildAssignmentFilter } from './assignment-filter';
import { useAssignmentEntitlement } from './use-assignment-entitlement';
import { usePendingConnections } from './use-pending-connections';

interface MonospacePolicy {
	id: string;
	name: string;
}

export interface InputPolicyAssignmentBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
}

const props = defineProps<InputPolicyAssignmentBlockProps>();

;

const store = useStore();
const { t } = useI18n();

const formBlockContext = injectFormBlockContext();
const { collection: policyCollection } = useCollectionByName('MonospacePolicy');
const { referencedItemKey: roleKey } = useRelationFormField(props);

const { allowedAssignableIds: allowedPolicyIds, canAssign, canDisconnectItem: canDisconnectPolicy, allowedDisconnections } = useAssignmentEntitlement(canAssignPolicy());

const roleId = computed(() => roleKey.value?.id);

const { data: rolePolicies, refresh: refreshRolePolicies } = store.RawMonospacePolicy.query((q) => {
	// An unsaved role has no attached policies, and a filter on an undefined id is dropped
	// during serialization, which would match every policy.
	if (!roleId.value) return q.many({ enabled: false });

	return q.many({
		filter: {
			roles: { _some: { roleId: roleId.value } },
		},
		// TODO(hannes) no-cache required here since we can't resolve relational filters locally
		fetchPolicy: 'no-cache',
	});
});

const currentPolicyIds = computed(() => rolePolicies.value.map(({ id }) => id));

const {
	pendingConnections,
	pendingDisconnections,
	effectiveIds,
	hasChanges,
	connect,
	disconnect,
	undo,
	getItemState,
	clearConnections,
	clearDisconnections,
} = usePendingConnections(currentPolicyIds);

// Load pending policies into the cache so the effective-id query can stay cache-only.
store.MonospacePolicy.query((q) => pendingConnections.value.size === 0
	? q.many({ enabled: false })
	: q.many({ filter: { id: { _in: [...pendingConnections.value] } } }));

const { data: policyItems } = store.MonospacePolicy.query((q) => q.many({ filter: { id: { _in: [...effectiveIds.value] } }, fetchPolicy: 'cache-only' }));

const displayItems = computed<RelatedDisplayItem<MonospacePolicy>[]>(() =>
	policyItems.value.map((item) => ({
		id: item.id,
		data: item,
		state: getItemState(item.id),
	})),
);

async function handleSelectPolicies() {
	if (!policyCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: policyCollection.value.id,
			selectionMode: 'multiple',
			filter: buildAssignmentFilter(allowedPolicyIds.value, [...effectiveIds.value]),
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result?.selection) {
		for (const key of result.selection) {
			connect(getKeyId(key)!);
		}
	}
}

function handleRemovePolicy(item: MonospacePolicy) {
	disconnect(item.id);
}

function handleUndoPolicy(item: MonospacePolicy) {
	undo(item.id);
}

formBlockContext.onSave(async ({ itemKey }: FormSaveContext): Promise<FormSaveError[]> => {
	const errors: FormSaveError[] = [];
	const savedRoleId = getKeyId({ id: itemKey.id })!;

	if (pendingConnections.value.size > 0) {
		try {
			await connectRolePolicies(savedRoleId, [...pendingConnections.value]);
			clearConnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.attach_policies_failed'), source: toStructuredError(error) });
		}
	}

	// Re-check the allowlist at save time so a staged removal can never detach a policy the user may not assign.
	const policyIdsToDisconnect = allowedDisconnections([...pendingDisconnections.value]);

	if (policyIdsToDisconnect.length > 0) {
		try {
			const junctionItems = store.MonospaceRolePolicy.peekMany({
				filter: { roleId: savedRoleId, policyId: { _in: policyIdsToDisconnect } },
			});

			await disconnectRolePolicies(savedRoleId, policyIdsToDisconnect);

			for (const item of junctionItems) {
				store.MonospaceRolePolicy.clearItem(store.MonospaceRolePolicy.getKey(item)!);
			}

			clearDisconnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.detach_policies_failed'), source: toStructuredError(error) });
		}
	}

	try {
		await refreshRolePolicies();
	}
	catch {
		// Refresh failure is non-fatal - user can manually refresh
	}

	return errors;
});

formBlockContext.reportHasChanges(() => hasChanges.value);
</script>

<template>
	<RelatedItemListCard
		v-if="policyCollection"
		:items="displayItems"
		:collection-id="policyCollection.id"
		:disabled
		:disabled-notice
		:show-edit="false"
		:show-create="false"
		:permissions="{ canConnect: canAssign, canDisconnectItem: canDisconnectPolicy }"
		show-add-existing
		@select="handleSelectPolicies"
		@remove="handleRemovePolicy"
		@undo="handleUndoPolicy"
	/>
</template>
